import { useState } from "react";
import { supabase } from "@/index";
import { useNavigate } from "react-router";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { PageHeading } from "./ui/page-heading";
import { PageContent } from "./ui/page-content";
import { ErrorDisplay } from "./ui/error";
import { SocialLogin } from "@/components/ui/social-login";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  async function handleSignIn() {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if 2FA is enabled
      const { data: factors } = await supabase.auth.mfa.listFactors();

      if (factors?.totp?.length) {
        setFactorId(factors.totp[0].id);
        await handle2FAChallenge(factors.totp[0].id);
      } else if (data?.user) {
        setUser(data.user);
        navigate("/");
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handle2FAChallenge(factorId: string) {
    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });

      if (challengeError) throw challengeError;

      setChallengeId(challengeData.id);
      setShow2FADialog(true);
    } catch (error: any) {
      setErrorMessage(error.message);
      showError(error.message);
    }
  }

  async function handleVerify2FA() {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      if (!factorId || !challengeId) {
        throw new Error("Missing authentication details");
      }

      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: verifyCode,
      });

      if (error) throw error;

      setShow2FADialog(false);
      setVerifyCode("");
      setUser(data.user);
      navigate("/");
    } catch (error: any) {
      setErrorMessage(error.message);
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  const showError = (message: string) => {
    setErrorMessage(message);
  };

  return (
    <>
      <PageHeading>
        <div className="text-center">Welcome to HireUp</div>
      </PageHeading>
      <PageContent>
        <div className="max-w-md mx-auto">
          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
            />
          </div>

          <div className="space-y-2 mt-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Password</label>
            </div>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
            />
          </div>

          <div className="text-right">
            <button
              onClick={() => navigate("/forgot-password")}
              type="button"
              className="text-sm text-primary hover:underline mt-2"
            >
              Forgot password?
            </button>
          </div>

          {errorMessage && (
            <ErrorDisplay message={errorMessage} className="mt-2" />
          )}

          <div className="space-y-4 mt-4">
            <Button
              onClick={handleSignIn}
              isLoading={isLoading}
              disabled={!email || !password || isLoading}
              className="w-full"
            >
              Log in
            </Button>
            <SocialLogin
              isLoading={isLoading}
              onError={(error) => showError(error)}
            />
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => navigate("/register")}
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Don't have an account? Register
              </Button>
            </div>
          </div>
        </div>
      </PageContent>

      <Dialog open={show2FADialog} onOpenChange={setShow2FADialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter the verification code from your authenticator app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder="Enter verification code"
                onKeyDown={(e) => e.key === "Enter" && handleVerify2FA()}
              />
            </div>
            <Button
              onClick={handleVerify2FA}
              isLoading={isLoading}
              disabled={!verifyCode || isLoading}
              className="w-full"
            >
              Verify
            </Button>
            {errorMessage && (
              <ErrorDisplay message={errorMessage} className="mt-2" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const LogIn = Auth;
